import type { ReactElement, ReactNode } from 'react'

import { cn } from '@gto/ui/cn'
import { Field } from '@gto/ui/field'

export type FieldShellProps = {
  label?: ReactNode
  /** Truthy → invalid styling + replaces the helper slot, matching MUI's error/helperText contract. */
  error?: ReactNode
  helperText?: ReactNode
  disabled?: boolean
  /** Also renders the MUI-style asterisk after the label (the control itself
   *  still receives the native `required` attribute from the caller). */
  required?: boolean
  className?: string
}

/** Base UI Field wires id/htmlFor/aria between label, control, and messages —
 *  the control must be passed through `Field.Control render` for that to work. */
export const FieldShell = ({
  label,
  error,
  helperText,
  disabled,
  required,
  className,
  messageSlot,
  control,
}: FieldShellProps & {
  control: ReactElement
  /** Extra message content rendered INSIDE `Field.Root`, below the helper/error
   *  slot. Pass `Field.Description` elements: each registers its own id with Base
   *  UI, which composes them into the control's `aria-describedby` — so an extra
   *  message is associated without the caller touching that attribute (and so
   *  without any way to clobber the helper/error association). Deliberately NOT
   *  on `FieldShellProps`: the select/textarea wrappers spread that type and
   *  would advertise a slot they do not forward. */
  messageSlot?: ReactNode
}) => {
  const hasError = error !== undefined && error !== null && error !== false && error !== ''

  return (
    <Field.Root
      className={cn(className)}
      disabled={disabled}
      invalid={hasError}
    >
      {label !== undefined && (
        <Field.Label>
          {label}
          {required === true && <span aria-hidden="true">&thinsp;*</span>}
        </Field.Label>
      )}
      <Field.Control render={control} />
      {hasError ? (
        <Field.Error match>{error}</Field.Error>
      ) : (
        helperText !== undefined && (
          <Field.Description className="px-2 text-[10px] leading-[166%] tracking-[0.4px] text-text-secondary">
            {helperText}
          </Field.Description>
        )
      )}
      {messageSlot}
    </Field.Root>
  )
}
